import React, { useEffect, useRef } from 'react';
import { Box, Typography, Paper, Skeleton, Chip, Tooltip, Alert } from '@mui/material';
import { Info, Warning, TrendingUp, TrendingDown } from '@mui/icons-material';
import type {
  PredictionMetricsData,
  AccuracyMetrics,
  EffectSizeMetrics
} from '../../lib/api/prediction-metrics';
import {
  formatAccuracyWithCI,
  getAccuracyHalfWidth,
  formatEffectSize,
  getSampleSizeCategory,
  getSampleSizeWarning,
  formatConsistency,
  getConsistencyLabel,
  getConsistencyColor
} from '../../lib/api/prediction-metrics';
import { DOCUMENTATION_URLS } from '../../lib/config/documentation';

interface PredictionMetricsCardProps {
  title: string;
  data: PredictionMetricsData | null;
  loading?: boolean;
  color?: string;
  horizon?: '1D' | '3D' | '7D';
  allHorizons?: Record<string, PredictionMetricsData | null>;
}

/** Get the localStorage key for consistency trend tracking */
function consistencyStorageKey(title: string, horizon: string): string {
  return `dac:consistency:${title}:${horizon}`;
}

/** Read previous consistency value from localStorage */
function getPreviousConsistency(title: string, horizon: string): number | null {
  try {
    const raw = localStorage.getItem(consistencyStorageKey(title, horizon));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return typeof parsed.value === 'number' ? parsed.value : null;
  } catch {
    return null;
  }
}

/** Save current consistency value to localStorage */
function saveConsistency(title: string, horizon: string, value: number): void {
  try {
    localStorage.setItem(
      consistencyStorageKey(title, horizon),
      JSON.stringify({ value, timestamp: Date.now() })
    );
  } catch {
    // localStorage unavailable — ignore
  }
}

export function PredictionMetricsCard({
  title,
  data,
  loading = false,
  color = '#757575',
  horizon,
  allHorizons
}: PredictionMetricsCardProps) {
  // Track consistency trend: compare current to previous localStorage value
  const prevConsistency = useRef<number | null>(null);
  const consistencyDelta = useRef<number | null>(null);

  useEffect(() => {
    if (!data || !horizon || data.consistency === null) return;
    const prev = getPreviousConsistency(title, horizon);
    prevConsistency.current = prev;
    if (prev !== null) {
      const delta = (data.consistency - prev) * 100;
      consistencyDelta.current = Math.abs(delta) >= 10 ? delta : null;
    }
    saveConsistency(title, horizon, data.consistency);
  }, [data, horizon, title]);
  if (loading) {
    return (
      <Paper sx={{ p: 2, height: '100%' }}>
        <Skeleton variant="text" width="80%" height={32} />
        <Skeleton variant="rectangular" height={120} sx={{ mt: 2 }} />
      </Paper>
    );
  }

  if (!data) {
    return (
      <Paper sx={{ p: 2, height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          No data available
        </Typography>
      </Paper>
    );
  }

  const sampleCategory = getSampleSizeCategory(data.sample_size);
  const warning = getSampleSizeWarning(data.sample_size);

  return (
    <Paper sx={{ p: 2, height: '100%', borderLeft: `4px solid ${color}` }}>
      {/* Title with sample size and horizon */}
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
        <Box>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
            {title}
          </Typography>
          {horizon && (
            <Chip
              label={`${horizon} Horizon`}
              size="small"
              variant="outlined"
              sx={{ fontSize: '0.7rem', height: 20, mb: 1 }}
            />
          )}
        </Box>
        <Chip
          label={`n=${data.sample_size}`}
          size="small"
          color={sampleCategory === 'reliable' ? 'success' : sampleCategory === 'small' ? 'warning' : 'default'}
          sx={{ fontSize: '0.75rem' }}
        />
      </Box>

      {/* Warning banner for small samples */}
      {warning && (
        <Alert severity={sampleCategory === 'insufficient' ? 'info' : 'warning'} sx={{ mb: 2, py: 0.5 }}>
          <Typography variant="caption">{warning}</Typography>
        </Alert>
      )}

      {/* Metrics grid */}
      <Box display="flex" flexDirection="column" gap={2}>
        {/* Accuracy with CI */}
        <MetricRow
          label="Accuracy"
          value={formatAccuracyWithCI(data.accuracy)}
          tooltip={
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                Accuracy with 95% Confidence Interval
              </Typography>
              <Typography variant="caption" display="block">
                Hit rate: {(data.accuracy.value * 100).toFixed(1)}%
              </Typography>
              <Typography variant="caption" display="block">
                95% CI: [{(data.accuracy.ci_lower * 100).toFixed(1)}%, {(data.accuracy.ci_upper * 100).toFixed(1)}%]
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 0.5, fontStyle: 'italic' }}>
                Method: Wilson score interval (accurate for small samples)
              </Typography>
            </Box>
          }
          greyedOut={false}
        />

        {/* Effect Size */}
        <MetricRow
          label="Effect Size"
          value={
            data.effect_size
              ? formatEffectSize(data.effect_size)
              : '—'
          }
          subtitle={data.effect_size ? '(when correct)' : undefined}
          tooltip={
            data.effect_size ? (
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Effect Size <em>(when we correctly predicted UP or DOWN)</em>
                </Typography>
                <Typography variant="caption" display="block">
                  <strong>Average absolute return when correct:</strong> {(data.effect_size.mean_abs_when_correct * 100).toFixed(1)}%
                </Typography>
                {data.effect_size.mean_signed_all !== null && (
                  <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                    <strong>Mean signed return across all signals:</strong> {data.effect_size.mean_signed_all >= 0 ? '+' : ''}{(data.effect_size.mean_signed_all * 100).toFixed(1)}%
                  </Typography>
                )}
                <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                  <em>This measures prediction quality, not trading outcomes.</em>
                  <br />
                  Higher values indicate stronger directional movement when the prediction is right.
                </Typography>
                {data.effect_size.winsorized && (
                  <Typography variant="caption" display="block" sx={{ mt: 1, fontStyle: 'italic', color: 'text.secondary' }}>
                    Note: Winsorized at {(data.effect_size.winsor_p ?? 0.02) * 100}% tails to reduce outlier impact
                  </Typography>
                )}
              </Box>
            ) : (
              <Box>
                <Typography variant="caption" display="block">
                  <strong>Insufficient data</strong> (n &lt; 5)
                </Typography>
                <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                  Effect size requires at least 5 mature signals for meaningful calculation.
                </Typography>
                <Typography variant="caption" display="block" sx={{ mt: 0.5, fontStyle: 'italic' }}>
                  This measures average return magnitude when predictions are correct, not financial performance.
                </Typography>
              </Box>
            )
          }
          greyedOut={!data.effect_size}
        />

        {/* Consistency (Phase 1B) */}
        <MetricRow
          label="Consistency"
          value={
            data.consistency !== null ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <span>{formatConsistency(data.consistency)}</span>
                {getConsistencyLabel(data.consistency) && (
                  <Chip
                    label={getConsistencyLabel(data.consistency)}
                    size="small"
                    color={getConsistencyColor(data.consistency)}
                    sx={{ fontSize: '0.7rem', height: 20 }}
                  />
                )}
                {consistencyDelta.current !== null && (
                  <Tooltip title={`Changed ${consistencyDelta.current > 0 ? '+' : ''}${consistencyDelta.current.toFixed(0)}% since last visit`}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                      {consistencyDelta.current > 0 ? (
                        <TrendingUp sx={{ fontSize: 16, color: 'success.main' }} />
                      ) : (
                        <TrendingDown sx={{ fontSize: 16, color: 'error.main' }} />
                      )}
                      <Typography
                        variant="caption"
                        sx={{ color: consistencyDelta.current > 0 ? 'success.main' : 'error.main', fontWeight: 600 }}
                      >
                        {consistencyDelta.current > 0 ? '+' : ''}{consistencyDelta.current.toFixed(0)}%
                      </Typography>
                    </Box>
                  </Tooltip>
                )}
              </Box>
            ) : '—'
          }
          subtitle={data.consistency === null ? '(more data needed)' : undefined}
          tooltip={
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                Consistency: How stable is accuracy over time?
              </Typography>
              {data.consistency !== null ? (
                <>
                  <Typography variant="caption" display="block">
                    <strong>Your signal:</strong> {formatConsistency(data.consistency)} consistency ({getConsistencyLabel(data.consistency)})
                  </Typography>
                  <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                    Measures week-to-week stability of prediction accuracy.
                    Higher values mean more reliable, predictable performance.
                  </Typography>
                  <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                    <strong>Scale:</strong>
                    <br />
                    • 0-30%: Volatile (unreliable week-to-week)
                    <br />
                    • 31-60%: Moderate (some variation)
                    <br />
                    • 61-85%: Stable (reliable)
                    <br />
                    • 86-100%: Very Stable (highly consistent)
                  </Typography>
                  {data.consistency_notes && data.consistency_notes.length > 0 && (
                    <Typography variant="caption" display="block" sx={{ mt: 1, fontStyle: 'italic', color: 'text.secondary' }}>
                      {data.consistency_notes.join(' • ')}
                    </Typography>
                  )}
                </>
              ) : (
                <>
                  <Typography variant="caption" display="block">
                    <strong>Insufficient data</strong> - Need ≥3 weeks with ≥3 signals each
                  </Typography>
                  {data.consistency_notes && data.consistency_notes.length > 0 && (
                    <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                      {data.consistency_notes.join(' • ')}
                    </Typography>
                  )}
                  <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                    Consistency will appear once sufficient weekly data accumulates.
                  </Typography>
                </>
              )}
            </Box>
          }
          greyedOut={data.consistency === null}
        />

        {/* Horizon Comparison */}
        {allHorizons && Object.keys(allHorizons).length > 1 && (
          <Box>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}
            >
              Consistency by Horizon
            </Typography>
            <Box display="flex" gap={0.75} mt={0.5} flexWrap="wrap">
              {(['1D', '3D', '7D'] as const).map(h => {
                const hData = allHorizons[h];
                const c = hData?.consistency ?? null;
                const isSelected = h === horizon;
                return (
                  <Chip
                    key={h}
                    label={`${h}: ${c !== null ? `${Math.round(c * 100)}%` : '—'}`}
                    size="small"
                    color={c !== null ? getConsistencyColor(c) : 'default'}
                    variant={isSelected ? 'filled' : 'outlined'}
                    sx={{
                      fontSize: '0.7rem',
                      height: 22,
                      fontWeight: isSelected ? 700 : 400,
                      opacity: c === null ? 0.5 : 1,
                    }}
                  />
                );
              })}
            </Box>
          </Box>
        )}
      </Box>

      {/* Learn more link */}
      <Box mt={1} sx={{ textAlign: 'center' }}>
        <Typography
          variant="caption"
          component="a"
          href={DOCUMENTATION_URLS.prediction_metrics}
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            color: 'primary.main',
            textDecoration: 'none',
            '&:hover': {
              textDecoration: 'underline'
            }
          }}
        >
          📖 Learn more about Wilson CI and effect size →
        </Typography>
      </Box>

      {/* Notes from API */}
      {data.notes && data.notes.length > 0 && (
        <Box mt={2} pt={2} borderTop={1} borderColor="divider">
          {data.notes.map((note, idx) => (
            <Typography key={idx} variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
              • {note}
            </Typography>
          ))}
        </Box>
      )}
    </Paper>
  );
}

interface MetricRowProps {
  label: string;
  value: string | React.ReactNode;
  subtitle?: string;
  tooltip: React.ReactNode;
  greyedOut: boolean;
}

function MetricRow({ label, value, subtitle, tooltip, greyedOut }: MetricRowProps) {
  return (
    <Box>
      <Box display="flex" alignItems="center" gap={0.5}>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}
        >
          {label}
        </Typography>
        <Tooltip title={tooltip} arrow placement="right">
          <Info
            sx={{
              fontSize: 14,
              color: 'text.disabled',
              cursor: 'help',
              '&:focus-visible': {
                outline: '2px solid',
                outlineColor: 'primary.main',
                outlineOffset: '1px'
              }
            }}
            tabIndex={0}
            role="button"
            aria-label={`More information about ${label}`}
            aria-describedby={`${label.toLowerCase().replace(/\s+/g, '-')}-tooltip`}
          />
        </Tooltip>
      </Box>
      <Typography
        variant="h5"
        sx={{
          fontWeight: 600,
          color: greyedOut ? 'text.disabled' : 'text.primary',
          fontFamily: 'monospace'
        }}
      >
        {value}
        {subtitle && (
          <Typography
            component="span"
            variant="caption"
            sx={{ ml: 1, color: greyedOut ? 'text.disabled' : 'text.secondary' }}
          >
            {subtitle}
          </Typography>
        )}
      </Typography>
    </Box>
  );
}
